import { Dwarf, type CallSite, type Location } from "./dwarf.ts";
import { EM_RISCV, EM_XTENSA, Elf } from "./elf.ts";
import { FrameTable, type Rule } from "./frame.ts";

export type { Location } from "./dwarf.ts";

/** A register dump and the stack memory the panic handler printed. */
export interface CpuState {
    /** RISC-V registers by number, x0 to x31. */
    registers: (number | undefined)[];
    pc: number;
    stackBase: number;
    stack: Uint8Array;
}

/** One frame of an unwound stack, innermost location first. */
export interface Frame {
    pc: number;
    locations: Location[];
}

const RA = 1;
const SP = 2;
const MAX_FRAMES = 64;

/** The firmware ELF file, read for decoding what the board prints. */
export class Symbols {
    readonly arch: "xtensa" | "riscv" | "other";
    /** Why there are no source lines, when the ELF file has no usable debug info. */
    readonly debugInfoProblem: string | undefined;
    readonly #elf: Elf;
    readonly #dwarf: Dwarf | undefined;
    #frames: FrameTable | null | undefined;

    constructor(bytes: Uint8Array) {
        this.#elf = new Elf(bytes);
        const machine = this.#elf.machine;
        this.arch = machine === EM_RISCV ? "riscv" : machine === EM_XTENSA ? "xtensa" : "other";
        let dwarf: Dwarf | undefined;
        let problem: string | undefined;
        try {
            dwarf = new Dwarf(this.#elf);
        } catch (e) {
            problem = e instanceof Error ? e.message : String(e);
        }
        this.#dwarf = dwarf;
        this.debugInfoProblem = problem;
    }

    isExecutable(address: number): boolean {
        return this.#elf.isExecutable(address);
    }

    /**
     * Where an address is in the source, innermost inlined function first.
     * Falls back to the symbol table for code without debug info, and is
     * empty for an address outside the firmware's code.
     */
    locate(address: number): Location[] {
        if (!this.isExecutable(address)) return [];
        const found = this.#dwarf?.locate(address) ?? [];
        const symbol = this.#elf.functionAt(address);
        if (found.length === 0) {
            return symbol === undefined ? [] : [{ func: symbol, file: undefined, line: undefined }];
        }
        const first = found[0]!;
        if (first.func === "??" && symbol !== undefined) found[0] = { ...first, func: symbol };
        return found;
    }

    /**
     * Unwinds a RISC-V stack dump with .debug_frame, the way GDB does it for
     * esp-idf-panic-decoder. Caller frames are looked up at pc - 1, since a
     * return address can be the first byte past the end of the call's function.
     */
    unwind(state: CpuState): Frame[] {
        const frames: Frame[] = [];
        const table = this.#frameTable();
        let registers = state.registers.slice();
        let pc = state.pc;
        for (let depth = 0; depth < MAX_FRAMES; depth++) {
            if (pc === 0 || !this.isExecutable(pc)) break;
            const lookup = depth === 0 ? pc : pc - 1;
            frames.push({ pc, locations: this.locate(lookup) });
            const sp = registers[SP];
            const rules = table?.stateAt(lookup);
            if (sp === undefined) break;
            if (rules === undefined || !rules.cfaKnown) {
                // Code without CFI, such as assembly: only the innermost frame can
                // be stepped over, by assuming it saved nothing and returns to ra.
                if (depth > 0) break;
                const ra = registers[RA];
                if (ra === undefined || ra === pc) break;
                pc = ra;
                continue;
            }
            const base = registers[rules.cfaRegister];
            if (base === undefined) break;
            const cfa = (base + rules.cfaOffset) >>> 0;
            const next = registers.slice();
            for (const [register, rule] of rules.rules) {
                if (rule.kind !== "same") next[register] = recover(rule, cfa, registers, state);
            }
            next[SP] = cfa;
            const returnAddress = next[rules.returnAddress];
            if (returnAddress === undefined || cfa < sp) break;
            if (cfa === sp && returnAddress === pc) break;
            registers = next;
            frames.push(...this.#tailCalls(lookup, returnAddress));
            pc = returnAddress;
        }
        return frames;
    }

    /**
     * Frames of functions that tail-called their way from the caller at
     * `returnAddress` to the callee at `calleePc`, as GDB rebuilds them from
     * the call sites in the debug info. Only a path that is unique counts.
     */
    #tailCalls(calleePc: number, returnAddress: number): Frame[] {
        const dwarf = this.#dwarf;
        const callee = dwarf?.functionAt(calleePc);
        if (dwarf === undefined || callee === undefined) return [];
        const site = dwarf.callSites(returnAddress - 1).find((s) => s.returnPc === returnAddress);
        if (site?.origin === undefined || site.origin === callee) return [];
        const path: Frame[] = [];
        let current: string = site.origin;
        for (let hops = 0; hops < 4; hops++) {
            const start = this.#elf.addressOf(current);
            if (start === undefined) return [];
            const tails = dwarf.callSites(start).filter((s) => s.tail && s.origin !== undefined);
            const direct: CallSite[] = tails.filter((s) => s.origin === callee);
            const tail: CallSite | undefined = direct.length > 0 ? direct[0] : tails[0];
            if (tail === undefined || direct.length > 1 || (direct.length === 0 && tails.length > 1)) {
                return [];
            }
            const pc = tail.returnPc ?? tail.callPc;
            if (pc === undefined) return [];
            path.push({ pc, locations: this.locate(tail.returnPc === undefined ? pc : pc - 1) });
            if (tail.origin === callee) return path.reverse();
            current = tail.origin!;
        }
        return [];
    }

    #frameTable(): FrameTable | undefined {
        if (this.#frames === undefined) {
            const section = this.#elf.section(".debug_frame");
            this.#frames = section === undefined ? null : new FrameTable(section);
        }
        return this.#frames ?? undefined;
    }
}

/** The caller's value of a register; undefined when it was not saved in the dump. */
function recover(rule: Rule, cfa: number, registers: (number | undefined)[], state: CpuState): number | undefined {
    switch (rule.kind) {
        case "offset":
            return readWord(state, cfa + rule.offset);
        case "valOffset":
            return (cfa + rule.offset) >>> 0;
        case "register":
            return registers[rule.register];
        default:
            return undefined;
    }
}

function readWord(state: CpuState, address: number): number | undefined {
    const at = address - state.stackBase;
    if (at < 0 || at + 4 > state.stack.length) return undefined;
    const s = state.stack;
    return (s[at]! | (s[at + 1]! << 8) | (s[at + 2]! << 16) | (s[at + 3]! << 24)) >>> 0;
}
